/**
 * Product Page JavaScript - Team Line Custom Theme
 * Handles product variant selection, image gallery, quantity selector, 
 * accordion toggles, sticky ATC panel, and add to cart
 */

class ProductPage {
  constructor() {
    // Main product form elements
    this.productForm = document.querySelector('[data-product-form]');
    this.variantInputs = document.querySelectorAll('[data-option-index]');
    this.quantityInput = document.querySelector('[data-quantity-input]');
    this.quantityDecrease = document.querySelector('[data-quantity-decrease]');
    this.quantityIncrease = document.querySelector('[data-quantity-increase]');
    this.addToCartButton = document.querySelector('[data-add-to-cart]');
    this.addToCartText = document.querySelector('[data-add-to-cart-text]');
    this.addToCartLoading = document.querySelector('[data-add-to-cart-loading]');
    this.productImage = document.querySelector('[data-product-image]');
    this.productPrice = document.querySelector('[data-product-price]');
    this.thumbnails = document.querySelectorAll('[data-thumbnail-index]');
    
    // Product details column (for sticky ATC trigger point)
    this.productDetails = document.querySelector('[data-product-details]');
    
    // Sticky ATC elements
    this.stickyAtc = document.querySelector('[data-sticky-atc]');
    this.stickyImage = document.querySelector('[data-sticky-image]');
    this.stickyImageMobile = document.querySelector('[data-sticky-image-mobile]');
    this.stickyVariant = document.querySelector('[data-sticky-variant]');
    this.stickyVariantMobile = document.querySelector('[data-sticky-variant-mobile]');
    this.stickyPrice = document.querySelector('[data-sticky-price]');
    this.stickyPriceMobile = document.querySelector('[data-sticky-price-mobile]');
    this.stickyAddToCartBtn = document.querySelector('[data-sticky-add-to-cart]');
    this.stickyAddToCartBtnMobile = document.querySelector('[data-sticky-add-to-cart-mobile]');
    this.stickyButtonText = document.querySelector('[data-sticky-button-text]');
    this.stickyButtonTextMobile = document.querySelector('[data-sticky-button-text-mobile]');

    this.variants = this.getVariants();
    this.currentVariant = null;
    this.stickyAtcVisible = false;

    this.init();
  }

  init() {
    // Initialize variant selection (requires productForm)
    if (this.productForm) {
      this.initVariantSelection();
      this.initQuantitySelector();
      this.initAddToCart();
    }

    // Initialize image gallery
    this.initImageGallery();

    // Initialize accordion toggles
    this.initAccordions();

    // Initialize sticky ATC
    this.initStickyAtc();
  }

  /**
   * Get all product variants from the window object
   */
  getVariants() {
    if (window.product && window.product.variants) {
      return window.product.variants;
    }
    return [];
  }

  /**
   * Initialize variant selection
   */
  initVariantSelection() {
    if (!this.variantInputs.length) return;

    this.variantInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.updateVariant();
      });
    });

    // Set initial variant
    this.updateVariant();
  }

  /**
   * Update selected variant based on option selections
   */
  updateVariant() {
    if (!window.product || !window.product.variants) return;

    const selectedOptions = [];
    
    // Collect selected option values in order
    this.variantInputs.forEach(input => {
      if (input.checked) {
        const optionIndex = parseInt(input.dataset.optionIndex);
        selectedOptions[optionIndex] = input.dataset.optionValue;
      }
    });

    // Find matching variant
    const variant = this.findVariant(selectedOptions);
    
    if (variant) {
      const variantInput = document.querySelector('[data-variant-id]');
      if (variantInput) {
        variantInput.value = variant.id;
        this.currentVariant = variant;
        this.updateVariantUI(variant);
        this.updateStickyAtc(variant);
        this.validateQuantity(); // Re-validate quantity for new variant's inventory
      }
    }
  }

  /**
   * Find variant based on selected options
   */
  findVariant(selectedOptions) {
    if (!window.product || !window.product.variants) return null;

    return window.product.variants.find(variant => {
      return variant.options.every((option, index) => {
        return selectedOptions[index] === option;
      });
    }) || null;
  }

  /**
   * Update UI based on selected variant
   */
  updateVariantUI(variant) {
    if (!variant) return;

    // Update variant option labels
    const variantSections = document.querySelectorAll('.product-form__variant');
    variantSections.forEach((section) => {
      const checkedInput = section.querySelector('input[type="radio"]:checked');
      const variantValueLabel = section.querySelector('[data-variant-label]');
      
      if (checkedInput && variantValueLabel) {
        const selectedValue = checkedInput.value;
        variantValueLabel.textContent = selectedValue;
      }
    });

    // Update variant option visual states
    this.variantInputs.forEach(input => {
      const label = input.nextElementSibling;
      if (label) {
        // Remove all state classes first
        label.classList.remove('tw-border-primary-teal', 'tw-bg-primary-teal', 'tw-text-white');
        label.classList.add('tw-border-neutral-200', 'tw-bg-white', 'tw-text-neutral-700');
        
        if (input.checked) {
          // Add selected state
          label.classList.remove('tw-border-neutral-200', 'tw-bg-white', 'tw-text-neutral-700');
          label.classList.add('tw-border-primary-teal', 'tw-bg-primary-teal', 'tw-text-white');
        }
      }
    });

    // Update dynamic availability
    this.updateOptionAvailability();

    // Update price
    if (this.productPrice) {
      const hasRegularSale = variant.compare_at_price > variant.price;
      const autoDiscount = window.autoDiscount || {};
      const hasAutoDiscount = autoDiscount.enabled && !hasRegularSale;
      
      if (hasAutoDiscount) {
        // Automatic discount pricing
        const discountMultiplier = (100 - autoDiscount.percentage) / 100;
        const discountedPrice = Math.round(variant.price * discountMultiplier);
        
        this.productPrice.innerHTML = `
          <span class="product-info__price--sale tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-neutral-900" data-auto-discount-price>${this.formatMoney(discountedPrice)}</span>
          <span class="product-info__price--compare tw-text-lg tw-text-neutral-400 tw-line-through" data-original-price>${this.formatMoney(variant.price)}</span>
          <span class="tw-bg-secondary-blue tw-text-white tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded" data-sale-badge>${autoDiscount.badgeText}</span>
        `;
      } else if (hasRegularSale) {
        // Regular sale pricing
        this.productPrice.innerHTML = `
          <span class="product-info__price--sale tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-neutral-900">${this.formatMoney(variant.price)}</span>
          <span class="product-info__price--compare tw-text-lg tw-text-neutral-400 tw-line-through">${this.formatMoney(variant.compare_at_price)}</span>
          <span class="tw-bg-secondary-blue tw-text-white tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded">SALE</span>
        `;
      } else {
        // Regular pricing
        this.productPrice.innerHTML = `
          <span class="product-info__price--regular tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-neutral-900">${this.formatMoney(variant.price)}</span>
        `;
      }
    }

    // Update add to cart button
    if (this.addToCartButton && this.addToCartText) {
      if (variant.available) {
        this.addToCartButton.disabled = false;
        this.addToCartText.textContent = window.variantStrings?.addToCart || 'Add to Bag';
      } else {
        this.addToCartButton.disabled = true;
        this.addToCartText.textContent = window.variantStrings?.soldOut || 'Sold Out';
      }
    }

    // Update image if variant has different image
    if (variant.featured_image && this.productImage) {
      this.productImage.src = variant.featured_image;
      this.productImage.srcset = `${variant.featured_image.replace('width=1200', 'width=600')} 600w, ${variant.featured_image.replace('width=1200', 'width=900')} 900w, ${variant.featured_image} 1200w`;
    }
  }

  /**
   * Update sticky ATC panel with current variant info
   */
  updateStickyAtc(variant) {
    if (!variant) return;

    // Update variant title
    const variantTitle = variant.title !== 'Default Title' ? variant.title : '';
    if (this.stickyVariant) this.stickyVariant.textContent = variantTitle;
    if (this.stickyVariantMobile) this.stickyVariantMobile.textContent = variantTitle;

    // Update price (with automatic discount if applicable)
    const hasRegularSale = variant.compare_at_price > variant.price;
    const autoDiscount = window.autoDiscount || {};
    const hasAutoDiscount = autoDiscount.enabled && !hasRegularSale;
    
    let displayPrice;
    if (hasAutoDiscount) {
      const discountMultiplier = (100 - autoDiscount.percentage) / 100;
      displayPrice = this.formatMoney(Math.round(variant.price * discountMultiplier));
    } else {
      displayPrice = this.formatMoney(variant.price);
    }
    
    if (this.stickyPrice) this.stickyPrice.textContent = displayPrice;
    if (this.stickyPriceMobile) this.stickyPriceMobile.textContent = displayPrice;

    // Update button state
    const buttonText = variant.available ? 'Add to Cart' : 'Sold Out';
    if (this.stickyButtonText) this.stickyButtonText.textContent = buttonText;
    if (this.stickyButtonTextMobile) this.stickyButtonTextMobile.textContent = buttonText;
    
    if (this.stickyAddToCartBtn) {
      this.stickyAddToCartBtn.disabled = !variant.available;
    }
    if (this.stickyAddToCartBtnMobile) {
      this.stickyAddToCartBtnMobile.disabled = !variant.available;
    }

    // Update image if variant has a different image
    if (variant.featured_image) {
      const thumbUrl = variant.featured_image.replace('width=1200', 'width=128');
      const thumbUrlMobile = variant.featured_image.replace('width=1200', 'width=96');
      if (this.stickyImage) this.stickyImage.src = thumbUrl;
      if (this.stickyImageMobile) this.stickyImageMobile.src = thumbUrlMobile;
    }
  }

  /**
   * Update option availability based on current selections
   */
  updateOptionAvailability() {
    if (!window.product || !window.product.variants || !window.product.options) return;

    const selectedOptions = [];
    
    this.variantInputs.forEach(input => {
      if (input.checked) {
        const optionIndex = parseInt(input.dataset.optionIndex);
        selectedOptions[optionIndex] = input.dataset.optionValue;
      }
    });

    window.product.options.forEach((option, optionIndex) => {
      const optionInputs = document.querySelectorAll(`[data-option-index="${optionIndex}"]`);
      
      optionInputs.forEach(input => {
        const value = input.dataset.optionValue;
        const label = input.nextElementSibling;
        if (!label) return;

        const isAvailable = this.isOptionValueAvailable(optionIndex, value, selectedOptions);
        
        if (isAvailable) {
          label.classList.remove('variant-pill--unavailable', 'tw-opacity-50');
          input.dataset.available = 'true';
        } else {
          label.classList.add('variant-pill--unavailable', 'tw-opacity-50');
          input.dataset.available = 'false';
        }
      });
    });
  }

  /**
   * Check if a specific option value is available given other selections
   */
  isOptionValueAvailable(optionIndex, value, selectedOptions) {
    if (!window.product || !window.product.variants) return true;

    return window.product.variants.some(variant => {
      if (variant.options[optionIndex] !== value) return false;
      if (!variant.available) return false;
      
      for (let i = 0; i < selectedOptions.length; i++) {
        if (i === optionIndex) continue;
        if (selectedOptions[i] && variant.options[i] !== selectedOptions[i]) {
          return false;
        }
      }
      
      return true;
    });
  }

  /**
   * Format money value
   */
  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Check if product allows backorders
   */
  allowsBackorder() {
    return window.product && window.product.tags && window.product.tags.includes('allow_backorder');
  }

  /**
   * Get max quantity allowed for current variant
   * Returns Infinity if backorders are allowed or inventory is not tracked
   */
  getMaxQuantity() {
    if (!this.currentVariant) return 999999;
    
    // If backorders are allowed, no limit
    if (this.allowsBackorder()) return 999999;
    
    // If inventory is not managed by Shopify, no limit
    if (this.currentVariant.inventory_management !== 'shopify') return 999999;
    
    // If inventory policy is 'continue', no limit
    if (this.currentVariant.inventory_policy === 'continue') return 999999;
    
    // Return available inventory quantity
    return Math.max(1, this.currentVariant.inventory_quantity || 0);
  }

  /**
   * Update quantity input max attribute and validate current value
   */
  validateQuantity() {
    if (!this.quantityInput) return;
    
    const maxQty = this.getMaxQuantity();
    const currentValue = parseInt(this.quantityInput.value) || 1;
    
    // Clamp value to valid range
    if (currentValue > maxQty) {
      this.quantityInput.value = maxQty;
    }
    if (currentValue < 1) {
      this.quantityInput.value = 1;
    }
  }

  /**
   * Initialize quantity selector
   */
  initQuantitySelector() {
    if (!this.quantityInput) return;

    if (this.quantityDecrease) {
      this.quantityDecrease.addEventListener('click', () => {
        const currentValue = parseInt(this.quantityInput.value) || 1;
        if (currentValue > 1) {
          this.quantityInput.value = currentValue - 1;
        }
      });
    }

    if (this.quantityIncrease) {
      this.quantityIncrease.addEventListener('click', () => {
        const currentValue = parseInt(this.quantityInput.value) || 1;
        const maxQty = this.getMaxQuantity();
        if (currentValue < maxQty) {
          this.quantityInput.value = currentValue + 1;
        }
      });
    }

    // Prevent invalid input and enforce max quantity
    this.quantityInput.addEventListener('change', () => {
      let value = parseInt(this.quantityInput.value);
      const maxQty = this.getMaxQuantity();
      
      if (isNaN(value) || value < 1) {
        value = 1;
      } else if (value > maxQty) {
        value = maxQty;
      }
      
      this.quantityInput.value = value;
    });
  }

  /**
   * Initialize image gallery
   */
  initImageGallery() {
    if (!this.thumbnails.length || !this.productImage) return;

    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => {
        const index = parseInt(thumbnail.dataset.thumbnailIndex);
        this.switchImage(index);
      });
    });
    
    // Handle "show all images" button
    const showAllBtn = document.querySelector('[data-show-all-images]');
    if (showAllBtn) {
      showAllBtn.addEventListener('click', () => {
        this.showAllThumbnails();
      });
    }
  }
  
  /**
   * Show all product thumbnails
   */
  showAllThumbnails() {
    const thumbnailsContainer = document.querySelector('[data-product-images]');
    if (!thumbnailsContainer) return;
    
    try {
      const images = JSON.parse(thumbnailsContainer.dataset.productImages);
      
      let thumbnailsHTML = '';
      images.forEach((image, index) => {
        const isActive = index === 0 ? ' tw-border-primary-teal' : ' tw-border-transparent';
        thumbnailsHTML += `
          <button
            type="button"
            class="product-gallery__thumbnail tw-w-16 tw-h-16 md:tw-w-20 md:tw-h-20 tw-rounded-lg tw-overflow-hidden tw-border-2 tw-transition-all tw-cursor-pointer hover:tw-border-primary-teal${isActive}"
            data-thumbnail-index="${index}"
            data-image-id="${image.id}"
            aria-label="View image ${index + 1} of ${images.length}"
            aria-pressed="${index === 0}"
          >
            <img
              src="${image.src.replace('width=1200', 'width=200')}"
              alt="${image.alt || ''}"
              class="product-gallery__thumbnail-image tw-w-full tw-h-full tw-object-cover"
              loading="lazy"
              width="200"
              height="200"
            >
          </button>
        `;
      });
      
      thumbnailsContainer.innerHTML = thumbnailsHTML;
      
      // Re-initialize thumbnail click handlers
      this.thumbnails = document.querySelectorAll('[data-thumbnail-index]');
      this.thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', () => {
          const index = parseInt(thumbnail.dataset.thumbnailIndex);
          this.switchImage(index);
        });
      });
      
    } catch (e) {
      console.error('Error showing all thumbnails:', e);
    }
  }

  /**
   * Switch main image based on thumbnail click
   */
  switchImage(index) {
    if (!this.productImage) return;

    const thumbnailsContainer = document.querySelector('[data-product-images]');
    if (thumbnailsContainer) {
      try {
        const images = JSON.parse(thumbnailsContainer.dataset.productImages);
        if (images[index]) {
          const image = images[index];
          this.productImage.src = image.src;
          this.productImage.srcset = image.srcset;
          this.productImage.alt = image.alt;
        }
      } catch (e) {
        console.error('Error parsing product images:', e);
        this.switchImageFallback(index);
      }
    } else {
      this.switchImageFallback(index);
    }

    // Update active thumbnail styling
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.remove('tw-border-primary-teal');
      thumb.classList.add('tw-border-transparent');
      if (i === index) {
        thumb.classList.add('tw-border-primary-teal');
        thumb.classList.remove('tw-border-transparent');
      }
    });
  }

  /**
   * Fallback method for switching images
   */
  switchImageFallback(index) {
    const images = Array.from(document.querySelectorAll('.product-gallery__thumbnail-image'));
    
    if (images[index] && this.productImage) {
      const thumbnailImage = images[index];
      const newImageSrc = thumbnailImage.src.replace('width=200', 'width=1200');
      
      this.productImage.src = newImageSrc;
      this.productImage.srcset = `${newImageSrc.replace('width=1200', 'width=600')} 600w, ${newImageSrc.replace('width=1200', 'width=900')} 900w, ${newImageSrc} 1200w`;
      this.productImage.alt = thumbnailImage.alt;
    }
  }

  /**
   * Initialize accordion toggles
   */
  initAccordions() {
    const accordionTriggers = document.querySelectorAll('[data-accordion-trigger]');
    
    accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const content = trigger.nextElementSibling;
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        
        // Toggle state
        trigger.setAttribute('aria-expanded', !isExpanded);
        
        if (isExpanded) {
          content.classList.add('tw-hidden');
          content.classList.remove('is-open');
        } else {
          content.classList.remove('tw-hidden');
          content.classList.add('is-open');
        }
      });
    });
  }

  /**
   * Initialize sticky ATC panel
   */
  initStickyAtc() {
    if (!this.stickyAtc) return;

    // Find the main Add to Cart button to use as trigger point
    const mainAddToCartBtn = this.addToCartButton;
    if (!mainAddToCartBtn) return;

    // Track if user has scrolled (to prevent showing on initial load)
    this.hasUserScrolled = false;
    this.initialButtonTop = 0;

    // Wait for page to fully render, then get button position
    setTimeout(() => {
      const rect = mainAddToCartBtn.getBoundingClientRect();
      this.initialButtonTop = rect.top + window.scrollY;
    }, 100);

    // Listen for scroll to enable sticky ATC functionality
    const onFirstScroll = () => {
      this.hasUserScrolled = true;
      window.removeEventListener('scroll', onFirstScroll);
    };
    window.addEventListener('scroll', onFirstScroll, { passive: true });

    // Create intersection observer to detect when Add to Cart button scrolls out of view
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px 0px 0px',
      threshold: 0
    };

    this.stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Only show sticky ATC if:
        // 1. User has scrolled at least once
        // 2. Add to Cart button is NOT visible
        // 3. User has scrolled DOWN past the button (not at top of page)
        const scrolledPastButton = window.scrollY > this.initialButtonTop - 100;
        
        if (!entry.isIntersecting && this.hasUserScrolled && scrolledPastButton) {
          this.showStickyAtc();
        } else {
          this.hideStickyAtc();
        }
      });
    }, observerOptions);

    // Observe the Add to Cart button
    this.stickyObserver.observe(mainAddToCartBtn);
    
    // Also add scroll listener as backup for more reliable behavior
    let lastScrollY = 0;
    window.addEventListener('scroll', () => {
      if (!this.hasUserScrolled) return;
      
      const currentScrollY = window.scrollY;
      const buttonRect = mainAddToCartBtn.getBoundingClientRect();
      const buttonIsVisible = buttonRect.top >= 0 && buttonRect.bottom <= window.innerHeight;
      
      // Show sticky when button is scrolled out of view (above viewport)
      if (buttonRect.bottom < 0 && currentScrollY > 100) {
        this.showStickyAtc();
      } else if (buttonIsVisible || currentScrollY < 100) {
        this.hideStickyAtc();
      }
      
      lastScrollY = currentScrollY;
    }, { passive: true });

    // Wire up sticky ATC buttons to submit the main form
    if (this.stickyAddToCartBtn) {
      this.stickyAddToCartBtn.addEventListener('click', () => {
        this.submitMainForm();
      });
    }
    if (this.stickyAddToCartBtnMobile) {
      this.stickyAddToCartBtnMobile.addEventListener('click', () => {
        this.submitMainForm();
      });
    }
  }

  /**
   * Show sticky ATC panel
   */
  showStickyAtc() {
    if (!this.stickyAtc || this.stickyAtcVisible) return;
    
    this.stickyAtc.classList.add('is-visible');
    this.stickyAtc.setAttribute('aria-hidden', 'false');
    this.stickyAtcVisible = true;
  }

  /**
   * Hide sticky ATC panel
   */
  hideStickyAtc() {
    if (!this.stickyAtc || !this.stickyAtcVisible) return;
    
    this.stickyAtc.classList.remove('is-visible');
    this.stickyAtc.setAttribute('aria-hidden', 'true');
    this.stickyAtcVisible = false;
  }

  /**
   * Submit the main product form (triggered by sticky ATC)
   */
  submitMainForm() {
    if (this.addToCartButton && !this.addToCartButton.disabled) {
      this.addToCartButton.click();
    }
  }

  /**
   * Initialize add to cart functionality
   * Note: Add to cart is handled by cart.js globally
   */
  initAddToCart() {
    // Cart functionality is handled by cart.js globally
  }
}

/**
 * Initialize horizontal scroll for recommendations
 */
function initRecommendationsScroll() {
  const scrollContainer = document.querySelector('.product-recommendations__grid');
  if (!scrollContainer) return;
  
  const checkScrollable = () => {
    return scrollContainer.scrollWidth > scrollContainer.clientWidth;
  };
  
  let isMouseDown = false;
  let hasMoved = false;
  let startX = 0;
  let startScrollLeft = 0;
  let clickedLinkHref = null;
  let preventNextClick = false;
  let dragVelocity = 0;
  let lastDragX = 0;
  let lastDragTime = 0;
  
  scrollContainer.style.scrollSnapType = 'none';
  
  const updateCursor = () => {
    if (checkScrollable()) {
      scrollContainer.style.cursor = isMouseDown ? 'grabbing' : 'grab';
    } else {
      scrollContainer.style.cursor = '';
    }
  };
  
  scrollContainer.addEventListener('click', (e) => {
    if (preventNextClick) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      preventNextClick = false;
    }
  }, true);
  
  const getCardWidth = () => {
    const firstCard = scrollContainer.querySelector('.product-card');
    if (!firstCard) return 200;
    const style = window.getComputedStyle(scrollContainer);
    const gap = parseFloat(style.gap) || 16;
    return firstCard.offsetWidth + gap;
  };
  
  // Snap to nearest card position based on drag direction and velocity
  const snapToNearestCard = (direction = 0) => {
    const cardWidth = getCardWidth();
    const currentScroll = scrollContainer.scrollLeft;
    const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    
    // Calculate which card position to snap to
    let cardIndex;
    if (direction > 0) {
      // Dragged left (scrolling right) - snap to next card
      cardIndex = Math.ceil(currentScroll / cardWidth);
    } else if (direction < 0) {
      // Dragged right (scrolling left) - snap to previous card
      cardIndex = Math.floor(currentScroll / cardWidth);
    } else {
      // No significant movement - snap to nearest
      cardIndex = Math.round(currentScroll / cardWidth);
    }
    
    let targetScroll = cardIndex * cardWidth;
    
    // Clamp to valid range
    targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));
    
    // Smooth scroll animation
    const startScroll = scrollContainer.scrollLeft;
    const distance = targetScroll - startScroll;
    
    // Skip animation if distance is very small
    if (Math.abs(distance) < 2) return;
    
    const duration = 400;
    const startTime = performance.now();
    
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    
    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      
      scrollContainer.scrollLeft = startScroll + (distance * easedProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };
    
    requestAnimationFrame(animateScroll);
  };
  
  updateCursor();
  window.addEventListener('resize', updateCursor);
  
  scrollContainer.querySelectorAll('img').forEach(img => {
    img.draggable = false;
  });
  
  scrollContainer.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button, [data-quick-add]')) return;
    if (!checkScrollable()) return;
    
    isMouseDown = true;
    hasMoved = false;
    preventNextClick = false;
    startX = e.pageX;
    startScrollLeft = scrollContainer.scrollLeft;
    lastDragX = e.pageX;
    lastDragTime = performance.now();
    dragVelocity = 0;
    
    const link = e.target.closest('a');
    clickedLinkHref = link ? link.href : null;
    
    scrollContainer.classList.add('is-dragging');
    scrollContainer.style.cursor = 'grabbing';
    scrollContainer.style.scrollBehavior = 'auto';
    
    e.preventDefault();
  });
  
  document.addEventListener('mousemove', (e) => {
    if (!isMouseDown) return;
    
    const deltaX = e.pageX - startX;
    const now = performance.now();
    const timeDelta = now - lastDragTime;
    
    if (Math.abs(deltaX) > 0) {
      hasMoved = true;
    }
    
    // Calculate velocity for snap direction
    if (timeDelta > 0) {
      dragVelocity = (e.pageX - lastDragX) / timeDelta;
    }
    lastDragX = e.pageX;
    lastDragTime = now;
    
    scrollContainer.scrollLeft = startScrollLeft - deltaX;
  });
  
  document.addEventListener('mouseup', () => {
    if (!isMouseDown) return;
    
    const wasDrag = hasMoved;
    const href = clickedLinkHref;
    const totalDragDistance = startX - lastDragX; // Positive = dragged left
    
    isMouseDown = false;
    clickedLinkHref = null;
    scrollContainer.classList.remove('is-dragging');
    scrollContainer.style.scrollBehavior = '';
    updateCursor();
    
    if (wasDrag) {
      preventNextClick = true;
      hasMoved = false;
      
      // Determine direction: positive = scrolling right, negative = scrolling left
      const direction = Math.abs(totalDragDistance) > 20 ? Math.sign(totalDragDistance) : 0;
      snapToNearestCard(direction);
      
      setTimeout(() => {
        preventNextClick = false;
      }, 100);
    } else if (href) {
      hasMoved = false;
      window.location.href = href;
    }
  });
  
  // Touch events for mobile
  let touchStartX = 0;
  let touchStartScrollLeft = 0;
  let touchHasMoved = false;
  let touchLastX = 0;
  
  scrollContainer.addEventListener('touchstart', (e) => {
    touchStartX = e.touches[0].pageX;
    touchLastX = touchStartX;
    touchStartScrollLeft = scrollContainer.scrollLeft;
    touchHasMoved = false;
    scrollContainer.style.scrollBehavior = 'auto';
  }, { passive: true });
  
  scrollContainer.addEventListener('touchmove', (e) => {
    const deltaX = e.touches[0].pageX - touchStartX;
    touchLastX = e.touches[0].pageX;
    if (Math.abs(deltaX) > 0) {
      touchHasMoved = true;
    }
    scrollContainer.scrollLeft = touchStartScrollLeft - deltaX;
  }, { passive: true });
  
  scrollContainer.addEventListener('touchend', () => {
    scrollContainer.style.scrollBehavior = '';
    if (touchHasMoved) {
      const totalDragDistance = touchStartX - touchLastX;
      const direction = Math.abs(totalDragDistance) > 20 ? Math.sign(totalDragDistance) : 0;
      snapToNearestCard(direction);
    }
  });
}

/**
 * Main init function
 */
function init() {
  // Initialize product page
  new ProductPage();
  
  // Initialize recommendations scroll
  initRecommendationsScroll();
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
